// src/components/tarifs/TarifsRecap.jsx
import * as React from 'react';
import {
  Paper, Box, Typography, Divider, Button, Chip, Stack,
  Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

// i18n
import { useI18n } from '../../i18n/I18nProvider';
import recapI18n from '../../i18n/components/tarifs/recap.i18n';
import pricelistI18n from '../../i18n/components/tarifs/pricelist.i18n';
import critereI18n from '../../i18n/pages/criterepage.i18n';

import RecapList from './RecapList';

const GREEN = '#0466c8';
const money = (n) => Number(n || 0).toFixed(2);

// ⚙️ Règle business : minimum 15 pièces (écrans) par envoi
const MIN_PIECES = 15;

// Même helper que dans PriceList pour nettoyer le préfixe "Ekran/Écran/screen"
const stripKnownPrefix = (name) =>
  String(name || '').replace(/^\s*(ekran|écran|ecran|screen)\s+/i, '');

  export default function TarifsRecap({
    summary,
    onClear,
    onRemoveLine, // ✅ NEW: permet de supprimer une ligne du panier (ex: lot expertise)
    stickyOffset = { xs: 72, md: 96 },
    highlightedId,
    onLineClick,
    showDetail = false,
    editingTransactionId = null,

    expertiseRequest,
    onExpertiseRequestConsumed,
  }) {
  const nav = useNavigate();
  const { t, register } = useI18n();

  React.useEffect(() => {
    // On enregistre recap + pricelist pour avoir accès à tarifs.pricelist.prefix.* et batchSort.title
    register('tarifs', recapI18n);
    register('tarifs', pricelistI18n);
    register('critere', critereI18n);
  }, [register]);

  const brandDisplay = React.useCallback((b) => {
    const other = t('tarifs.recap.brand.other');
    if (!b) return other;
    const s = String(b).trim();
    if (!s) return other;

    const low = s.toLowerCase();

    // 🔴 Cas spécial : ancien slogan "Vendez en 1 clic" (ou son équivalent)
    // → on remplace par le titre batchSort i18n (comme dans PriceList)
    if (
      low.includes('vendez en 1 clic') ||
      low.includes('wyślij, a my posortujemy')
    ) {
      return t('tarifs.pricelist.batchSort.title');
    }

    return s[0].toUpperCase() + s.slice(1);
  }, [t]);

  const {
    lines: rawLines = [],
    expertiseNotes: rawExpertiseNotes = {},
  } = summary || {};

  const lines = rawLines
    .map((l) => {
      const id = String(l.id || '');
      const qty = Number(l.qty) || 0;
      const price = Number(l.price) || 0;
      let brand = l.brand;
      if ((!brand || String(brand).trim() === '') && id.startsWith('brand:')) {
        brand = l.label || '';
      }
      const brandNorm = String(brand || t('tarifs.recap.brand.other')).toLowerCase();
      return { ...l, id, qty, price, brand: brandNorm };
    })
    .filter((l) => l.qty > 0);

  let totalHt = 0;
  let totalQty = 0;
  let unitPiecesQty = 0; // toutes les pièces à l’unité (hors lignes brand:*)
  const brandCounts = new Map();

  lines.forEach(l => {
    totalHt += l.price * l.qty;
    totalQty += l.qty;

    if (!l.id.startsWith('brand:')) {
      unitPiecesQty += l.qty;
    }

    brandCounts.set(l.brand, (brandCounts.get(l.brand) || 0) + l.qty);
  });

  const brandsSorted = Array.from(brandCounts.entries())
    .map(([key, qty]) => ({ key, qty, label: brandDisplay(key) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const [open, setOpen] = React.useState(false);
  const [criteriaOpen, setCriteriaOpen] = React.useState(false);
  const [criteriaConfirmation, setCriteriaConfirmation] = React.useState(false);
  const [pendingRecapState, setPendingRecapState] = React.useState(null);

  // ✨ Popup "minimum 15 écrans"
  const [minModelsDialogOpen, setMinModelsDialogOpen] = React.useState(false);

  // ✨ Notes d'expertise pour les lignes "brand:*" sur expertise
  const [expertDialogOpen, setExpertDialogOpen] = React.useState(false);
  const [expertTargetLineId, setExpertTargetLineId] = React.useState(null);

  // ➜ nombre de colis (optionnel)
  const [expertParcels, setExpertParcels] = React.useState('');

  // ➜ nombre de pièces approximatif (requis)
  const [expertPieces, setExpertPieces] = React.useState('');

  // ➜ poids approximatif en kg (optionnel)
  const [expertWeightKg, setExpertWeightKg] = React.useState('');

  const [expertNotes, setExpertNotes] = React.useState({});
  // ✅ Si true, la popup a été ouverte depuis la sélection (pas depuis "Valider")
  const [expertFromSelection, setExpertFromSelection] = React.useState(false);

  // ✅ Hydratation initiale depuis summary.expertiseNotes (quand on revient du /recap)
  React.useEffect(() => {
    if (!rawExpertiseNotes || typeof rawExpertiseNotes !== 'object') return;

    const normalized = {};
    Object.entries(rawExpertiseNotes).forEach(([lineId, note]) => {
      if (!note || typeof note !== 'object') return;

      const parcelsCount = String(
        note.parcelsCount ?? note.parcels ?? ''
      ).trim();
      const weightKg = String(
        note.weightKg ?? note.weight ?? ''
      ).trim();
      const piecesCount = String(
        note.piecesCount ?? note.pieces ?? ''
      ).trim();

      normalized[lineId] = {
        parcelsCount,
        weightKg,
        piecesCount,
      };
    });

    if (Object.keys(normalized).length > 0) {
      setExpertNotes(prev => ({ ...prev, ...normalized }));
    }
  }, [rawExpertiseNotes]);
  const openExpertiseDialog = React.useCallback((lineId, { fromSelection = false } = {}) => {
    if (!lineId) return;

    const existing = expertNotes[String(lineId)] || {};

    setExpertTargetLineId(String(lineId));
    setExpertParcels(String(existing.parcelsCount ?? existing.parcels ?? '').trim());
    setExpertPieces(String(existing.piecesCount ?? existing.pieces ?? '').trim());
    setExpertWeightKg(String(existing.weightKg ?? existing.weight ?? '').trim());

    setExpertFromSelection(!!fromSelection);
    setExpertDialogOpen(true);
  }, [expertNotes]);

  // ✅ Ouvre la popup d'expertise dès que PriceList la demande (sélection du lot)
  React.useEffect(() => {
    if (!expertiseRequest?.lineId) return;

    // On ouvre la popup en mode "sélection" (pas de navigation après confirm)
    openExpertiseDialog(expertiseRequest.lineId, { fromSelection: true });

    // reset côté parent (optionnel)
    if (onExpertiseRequestConsumed) onExpertiseRequestConsumed();
  }, [
    expertiseRequest?.nonce,
    expertiseRequest?.lineId,
    openExpertiseDialog,
    onExpertiseRequestConsumed,
  ]);

  // ✅ Fallback robuste: si le parent ne câble pas `expertiseRequest`,
  // on ouvre quand même la popup dès que le lot expertise passe de 0 -> 1.
  const prevExpertQtyRef = React.useRef(0);

  React.useEffect(() => {
    // ligne "Envoie, on trie pour toi" sur expertise (prix <= 0)
    const expertiseLine = lines.find(
      (l) =>
        String(l.id || '').startsWith('brand:') &&
        (Number(l.price) || 0) <= 0
    );

    const currentQty = Number(expertiseLine?.qty || 0) || 0;
    const prevQty = Number(prevExpertQtyRef.current || 0) || 0;
    prevExpertQtyRef.current = currentQty;

    // On n'ouvre que lors de la transition 0 -> >0
    if (!expertiseLine) return;
    if (!(prevQty === 0 && currentQty > 0)) return;

    const existing = expertNotes[String(expertiseLine.id)] || {};
    const piecesRaw = String(existing.piecesCount ?? existing.pieces ?? '').trim();
    const approxPieces = Number(piecesRaw.replace(',', '.'));

    // Si déjà rempli, pas besoin d'ouvrir
    if (piecesRaw && !Number.isNaN(approxPieces) && approxPieces > 0) return;

    // Ouvre en mode "sélection" (pas de navigation après confirm)
    openExpertiseDialog(expertiseLine.id, { fromSelection: true });
  }, [lines, expertNotes, openExpertiseDialog]);

  // ✅ Injecte l'expertiseNote dans la ligne brand:* (pour que le back la stocke)
  // notesOverride permet d'injecter immédiatement les valeurs fraîchement confirmées (ex: nextNotes)
  const buildLinesWithExpertiseNote = React.useCallback((baseLines, notesOverride = null) => {
    const notes = notesOverride || expertNotes;
    if (!Array.isArray(baseLines)) return [];

    return baseLines.map((l) => {
      const isExpertise =
        String(l?.id || '').startsWith('brand:') &&
        (Number(l?.price) || 0) <= 0 &&
        (Number(l?.qty) || 0) > 0;

      if (!isExpertise) return l;

      const note = notes[String(l.id)] || {};
      const parcelsCount = String(note.parcelsCount ?? '').trim();
      const piecesCount = String(note.piecesCount ?? '').trim();
      const weightKg = String(note.weightKg ?? '').trim();

      return {
        ...l,
        // ⬇️ IMPORTANT: le back lit `line.expertiseNote`
        expertiseNote: {
          parcelsCount,
          piecesCount,
          weightKg,
        },
      };
    });
  }, [expertNotes]);

  const showCriteriaBeforeRecap = (linesWithNote, notes = expertNotes) => {
    setPendingRecapState({ summary: { lines: linesWithNote, expertiseNotes: notes }, from: '/tarifs', editingTransactionId });
    setCriteriaConfirmation(true);
    setCriteriaOpen(true);
  };

  const handleValidate = () => {
    if (totalQty === 0) return;

    // 1) On cherche une ligne "marque / lot" sur expertise (prix <= 0)
    const expertiseLine = lines.find(l =>
      l.id.startsWith('brand:') &&
      (Number(l.price) || 0) <= 0 &&
      l.qty > 0
    );

    // 👉 Cas "Vendez en 1 clic" / expertise
    if (expertiseLine) {
      const existing = expertNotes[expertiseLine.id] || {};
      const piecesRaw = String(existing.piecesCount ?? existing.pieces ?? '').trim();
      const approxPieces = Number(piecesRaw.replace(',', '.'));

      // ✅ Si déjà rempli (car demandé à la sélection), on NE redemande PAS au validate
      if (piecesRaw && !Number.isNaN(approxPieces) && approxPieces > 0) {
        const totalWithExpertise = unitPiecesQty + approxPieces;
        if (totalWithExpertise < MIN_PIECES) {
          setMinModelsDialogOpen(true);
          return;
        }

        const linesWithNote = buildLinesWithExpertiseNote(lines);

        showCriteriaBeforeRecap(linesWithNote);
        return;
      }

      // ⛔ On ne redemande PAS lors de la validation.
      // La popup est ouverte dès la sélection du lot. Si l'info manque, on bloque simplement.
      return;
    }

    // 👉 Cas standard : pas de lot expertise → on applique la règle des 15 pièces
    if (unitPiecesQty < MIN_PIECES) {
      setMinModelsDialogOpen(true);
      return;
    }

    const linesWithNote = buildLinesWithExpertiseNote(lines);

    showCriteriaBeforeRecap(linesWithNote);
  };

  const handleConfirmExpertNote = () => {
    if (!expertTargetLineId) {
      setExpertDialogOpen(false);
      return;
    }

    const parcelsVal = String(expertParcels || '').trim();
    const weightValRaw = String(expertWeightKg || '').trim();
    const piecesValRaw = String(expertPieces || '').trim();

    const approxPieces = Number(piecesValRaw.replace(',', '.'));
    const weightN = Number(weightValRaw.replace(',', '.'));
    const parcelsN = Number(parcelsVal.replace(',', '.'));

    // 🔒 On exige que TOUS les champs soient remplis et valides
    if (
      !piecesValRaw || Number.isNaN(approxPieces) || approxPieces <= 0 ||
      !weightValRaw || Number.isNaN(weightN) || weightN <= 0 ||
      !parcelsVal || Number.isNaN(parcelsN) || parcelsN < 1
    ) {
      return;
    }

    const nextNotes = {
      ...expertNotes,
      [expertTargetLineId]: {
        parcelsCount: parcelsVal,
        weightKg: weightValRaw,
        piecesCount: piecesValRaw,
      },
    };

    setExpertNotes(nextNotes);
    setExpertDialogOpen(false);

    // ✅ Si la popup a été ouverte depuis la sélection (PriceList),
    // on enregistre puis on ferme sans redirection (la navigation se fera au clic "Valider")
    if (expertFromSelection) {
      setExpertFromSelection(false);
      return;
    }

    // ✅ Règle business : sélection à l’unité + estimation expertise ≥ 15
    const totalWithExpertise = unitPiecesQty + approxPieces;
    if (totalWithExpertise < MIN_PIECES) {
      // On affiche la popup "minimum 15 écrans"
      setMinModelsDialogOpen(true);
      return;
    }

    const linesWithNote = buildLinesWithExpertiseNote(lines, nextNotes);

    showCriteriaBeforeRecap(linesWithNote, nextNotes);
  };

  const currentExpertLine = React.useMemo(
    () => lines.find(l => l.id === expertTargetLineId),
    [lines, expertTargetLineId]
  );

  // ✅ validité du formulaire d'expertise : TOUS les champs requis
  // - pieces: nombre > 0
  // - weightKg: nombre > 0
  // - parcels: entier >= 1
  const isExpertFormValid = (() => {
    const piecesValRaw = String(expertPieces || '').trim();
    const weightValRaw = String(expertWeightKg || '').trim();
    const parcelsValRaw = String(expertParcels || '').trim();

    const piecesN = Number(piecesValRaw.replace(',', '.'));
    const weightN = Number(weightValRaw.replace(',', '.'));
    const parcelsN = Number(parcelsValRaw.replace(',', '.'));

    const piecesOk = !!piecesValRaw && !Number.isNaN(piecesN) && piecesN > 0;
    const weightOk = !!weightValRaw && !Number.isNaN(weightN) && weightN > 0;
    const parcelsOk = !!parcelsValRaw && !Number.isNaN(parcelsN) && parcelsN >= 1;

    return piecesOk && weightOk && parcelsOk;
  })();

  const closeExpertDialog = React.useCallback((reason = 'close') => {
    // ✅ Si on ferme la popup sans valeur valide => on retire la sélection du lot expertise
    // (clic dehors, ESC, bouton Retour)
    const shouldRemove =
      (reason === 'backdropClick' || reason === 'escapeKeyDown' || reason === 'close') &&
      !isExpertFormValid;

    if (shouldRemove) {
      // ✅ Fallback si l'id n'est pas encore hydraté
      const fallbackExpertiseLine = lines.find(
        (l) =>
          String(l.id || '').startsWith('brand:') &&
          (Number(l.price) || 0) <= 0 &&
          (Number(l.qty) || 0) > 0
      );

      const targetId = expertTargetLineId || fallbackExpertiseLine?.id;
      if (targetId) {
        onRemoveLine?.(String(targetId)); // ✅ retire du panier => disparaît du RecapList
      }
    }

    setExpertDialogOpen(false);
    setExpertFromSelection(false);
    setExpertTargetLineId(null);
    setExpertParcels('');
    setExpertPieces('');
    setExpertWeightKg('');
  }, [
    expertTargetLineId,
    isExpertFormValid,
    onRemoveLine,
    lines,
  ]);

  // 👇 Helper pour afficher le label modèle comme sur la liste tarifs
  const buildModelLabel = React.useCallback((line) => {
    const rawLabel = String(line.label || '');
    const cleaned = stripKnownPrefix(rawLabel);
    const prefix = t('tarifs.pricelist.prefix.screen'); // on considère que les model:* sont des écrans
    return `${prefix} ${cleaned}`.trim();
  }, [t]);

  // 👇 Helper pour le label "brand:*" → on force le même titre que dans la liste tarifs
  const buildBrandLabel = React.useCallback(() => {
    return t('tarifs.pricelist.batchSort.title');
  }, [t]);

  const expertiseLineSelected = React.useMemo(() => {
    return lines.find(
      (l) =>
        String(l.id || '').startsWith('brand:') &&
        (Number(l.price) || 0) <= 0 &&
        (Number(l.qty) || 0) > 0
    );
  }, [lines]);

  const expertiseMissing = React.useMemo(() => {
    if (!expertiseLineSelected) return false;
    const existing = expertNotes[String(expertiseLineSelected.id)] || {};
    const piecesRaw = String(existing.piecesCount ?? existing.pieces ?? '').trim();
    const approxPieces = Number(piecesRaw.replace(',', '.'));
    return !(piecesRaw && !Number.isNaN(approxPieces) && approxPieces > 0);
  }, [expertiseLineSelected, expertNotes]);

  const hasDetail = showDetail && lines.length > 0;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 2,
          position: 'sticky',
          top: stickyOffset,
          zIndex: 1,
        }}
      >
        <Typography sx={{ fontWeight: 900, mb: 1 }}>
          {t('tarifs.recap.title')}
        </Typography>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => { setCriteriaConfirmation(false); setPendingRecapState(null); setCriteriaOpen(true); }}
          sx={{ mb: 1.25, fontWeight: 800, borderWidth: 2 }}
        >
          {t('critere.dialog.title')}
        </Button>

        {brandsSorted.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {brandsSorted.map(b => (
                <Chip
                  key={b.key}
                  size="small"
                  label={`${b.label}: ${b.qty}`}
                  sx={{ bgcolor: '#f6f7f6' }}
                />
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ fontSize: 14, lineHeight: 1.9 }}>
          <Box>{t('tarifs.recap.count.totalItems')} : <b>{totalQty}</b></Box>
          <Box>{t('tarifs.recap.total')} : <b>{money(totalHt)} €</b></Box>
        </Box>
        <Button
          fullWidth
          variant="contained"
          onClick={handleValidate}
          disabled={totalQty === 0 || expertiseMissing}
          sx={{ mt: 1, bgcolor: GREEN, '&:hover': { bgcolor: '#00478fff' } }}
        >
          {t('tarifs.recap.actions.validate')}
        </Button>
        {expertiseMissing && (
          <Typography sx={{ mt: 1, fontSize: 12, color: 'text.secondary' }}>
            {t('tarifs.recap.expertise.dialogSubtitle')}
          </Typography>
        )}

        {hasDetail && (
          <Box sx={{ mt: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.5 }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                {t('tarifs.recap.detail.title', 'Détail du panier')}
              </Typography>

              {totalQty > 0 && (
                <Button
                  onClick={onClear}
                  size="small"
                  variant="text"
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'none',
                    px: 0,
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  {t('tarifs.recap.actions.removeSelection')}
                </Button>
              )}
            </Stack>

            <RecapList
              summary={summary}
              highlightedId={highlightedId}
              onLineClick={onLineClick}
            />
          </Box>
        )}

        {/* ========== DIALOG DÉTAIL ========== */}
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {t('tarifs.recap.detail.title')}
            <IconButton onClick={() => setOpen(false)} size="small" aria-label={t('tarifs.recap.actions.close')}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {lines.length === 0 ? (
              <Typography sx={{ color: 'text.secondary' }}>
                {t('tarifs.recap.detail.empty')}
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small" aria-label={t('tarifs.recap.detail.aria')}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('tarifs.recap.table.article')}</TableCell>
                      <TableCell align="right">{t('tarifs.recap.table.unitPrice')}</TableCell>
                      <TableCell align="right">{t('tarifs.recap.table.qty')}</TableCell>
                      <TableCell align="right">{t('tarifs.recap.table.total')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map(l => {
                      const isModel = l.id.startsWith('model:');
                      const isBrand = l.id.startsWith('brand:');
                      const displayLabel = isModel
                        ? buildModelLabel(l)
                        : isBrand
                          ? buildBrandLabel()
                          : l.label;

                      let subtitle = '';
                      if (isModel) {
                        subtitle =
                          t('tarifs.recap.kind.device') +
                          (l.brand ? ` · ${brandDisplay(l.brand)}` : '');
                      } else if (l.id.startsWith('component:')) {
                        subtitle =
                          t('tarifs.recap.kind.part') +
                          (l.brand ? ` · ${brandDisplay(l.brand)}` : '');
                      } else if (isBrand) {
                        // même texte que sur la carte batchSort
                        subtitle = buildBrandLabel();
                      } else if (l.brand) {
                        subtitle = brandDisplay(l.brand);
                      }

                      return (
                        <TableRow key={l.id} hover>
                          <TableCell sx={{ maxWidth: 360 }}>
                            <Typography sx={{ fontWeight: 600 }}>{displayLabel}</Typography>
                            {subtitle && (
                              <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                                {subtitle}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{money(l.price)}</TableCell>
                          <TableCell align="right">{l.qty}</TableCell>
                          <TableCell align="right"><b>{money(l.price * l.qty)}</b></TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell align="right"><b>{t('tarifs.recap.table.total')}</b></TableCell>
                      <TableCell align="right"><b>{money(totalHt)} €</b></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1 }}>
            <Button
              onClick={() => setOpen(false)}
              variant="contained"
              size="small"
              sx={{ textTransform: 'none', bgcolor: GREEN, '&:hover': { bgcolor: '#00478fff' } }}
            >
              {t('tarifs.recap.actions.close')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ========== DIALOG EXPERTISE (nombre de colis / nombre de pièces) ========== */}
          <Dialog
            open={expertDialogOpen}
            onClose={(_, reason) => closeExpertDialog(reason)}
            maxWidth="xs"
            fullWidth
          >
          <DialogTitle
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              pb: 1,
            }}
          >
            {t('tarifs.recap.expertise.dialogTitle')}
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 1.5 }}>

            <Typography sx={{ mb: 1.75, fontSize: 13, color: 'text.secondary' }}>
              {t('tarifs.recap.expertise.dialogSubtitle')}
            </Typography>

            <Stack spacing={1.25} sx={{ mb: 1.5 }}>
              {/* 1ère ligne : pièces + poids sur la même ligne */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label={t('tarifs.recap.expertise.piecesLabel')}
                  placeholder={t('tarifs.recap.expertise.piecesPlaceholder')}
                  value={expertPieces}
                  onChange={(e) => setExpertPieces(e.target.value)}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: '1' }}
                />

                <TextField
                  label={t('tarifs.recap.expertise.weightKgLabel', 'Poids approximatif (kg)')}
                  placeholder={t('tarifs.recap.expertise.weightKgPlaceholder', 'Ex. 18')}
                  value={expertWeightKg}
                  onChange={(e) => setExpertWeightKg(e.target.value)}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0, step: '0.1' }}
                />
              </Box>

              <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: -0.5 }}>
                {t('tarifs.recap.expertise.minPiecesHint')}
              </Typography>

              {/* 2ème ligne : nombre de colis */}
              <TextField
                label={t('tarifs.recap.expertise.parcelsLabel')}
                placeholder={t('tarifs.recap.expertise.parcelsPlaceholder')}
                value={expertParcels}
                onChange={(e) => setExpertParcels(e.target.value)}
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0, step: '1' }}
              />

              {/* 🔥 Mise en avant claire de la limite 30 kg / colis */}
              <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: -0.5 }}>
                {t('tarifs.recap.expertise.weightHint')}
              </Typography>
            </Stack>

            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {t('tarifs.recap.expertise.globalHelper')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1 }}>
          <Button
            onClick={() => closeExpertDialog('close')}
            sx={{ textTransform: 'none' }}
          >
            {t('tarifs.recap.expertise.cancel')}
          </Button>
            <Button
              onClick={handleConfirmExpertNote}
              variant="contained"
              disabled={!isExpertFormValid}
              sx={{
                textTransform: 'none',
                bgcolor: GREEN,
                '&:hover': { bgcolor: '#00478fff' },
              }}
            >
              {t('tarifs.recap.expertise.confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ========== DIALOG MINIMUM 15 ÉCRANS (erreur logistique) ========== */}
        <Dialog
          open={minModelsDialogOpen}
          onClose={() => setMinModelsDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle
            sx={{
              fontWeight: 800,
              textAlign: 'center',
              pb: 1,
            }}
          >
            {t('tarifs.recap.validation.minModelsTitle')}
          </DialogTitle>
          <DialogContent dividers sx={{ pt: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {t('tarifs.recap.validation.minModelsBody')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2, py: 1, justifyContent: 'center' }}>
            <Button
              onClick={() => setMinModelsDialogOpen(false)}
              sx={{ textTransform: 'none' }}
              autoFocus
            >
              {t('tarifs.recap.validation.minModelsCta')}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>

      <Dialog open={criteriaOpen} onClose={() => setCriteriaOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 900, color: GREEN }}>{t('critere.dialog.title')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography fontWeight={800}>{t('critere.dialog.screens')}</Typography>
            <Typography><b>{t('critere.examples.cracks.title')} :</b> {t('critere.dialog.gradeA')}</Typography>
            <Typography><b>{t('critere.examples.spots.title')} :</b> {t('critere.dialog.gradeB')}</Typography>
            <Typography><b>{t('critere.examples.touch.title')} :</b> {t('critere.dialog.gradeC')}</Typography>
            <Alert severity="warning" sx={{ fontWeight: 700 }}>{t('critere.dialog.originals')}</Alert>
            <Divider />
            <Typography><b>{t('critere.dialog.motherboardsLabel')} :</b> {t('critere.dialog.motherboards')}</Typography>
            <Typography><b>{t('critere.dialog.partsLabel')} :</b> {t('critere.dialog.parts')}</Typography>
            <Alert severity="info">
              {t('critere.dialog.disclaimer')}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCriteriaOpen(false)}>{criteriaConfirmation ? t('critere.dialog.back') : t('critere.dialog.close')}</Button>
          {criteriaConfirmation && <Button variant="contained" onClick={() => { const state = pendingRecapState; setCriteriaOpen(false); setPendingRecapState(null); if (state) nav('/recap', { state }); }} sx={{ bgcolor: GREEN }}>{t('critere.dialog.understand')}</Button>}
        </DialogActions>
      </Dialog>
    </>
  );
}
